import { useState, useRef } from "react";
import { Button } from "@/components/ui/button";
import { cn } from "@/lib/utils";
import { TripComponent } from "@shared/schema";
import { FileUpIcon, Loader2 } from "lucide-react";
import { Progress } from "@/components/ui/progress";

interface PdfUploaderProps {
  tripId: number;
  onProcessed: (components: TripComponent[]) => void;
  onProcessingStart: () => void;
  onError: (error: string) => void;
}

export function PdfUploader({ tripId, onProcessed, onProcessingStart, onError }: PdfUploaderProps) {
  const [dragActive, setDragActive] = useState(false);
  const [uploading, setUploading] = useState(false);
  const [progress, setProgress] = useState(0);
  const [selectedFiles, setSelectedFiles] = useState<File[]>([]);
  const fileInputRef = useRef<HTMLInputElement>(null);

  // Handle drag events
  const handleDrag = (e: React.DragEvent<HTMLDivElement>) => {
    e.preventDefault();
    e.stopPropagation();
    
    if (e.type === "dragenter" || e.type === "dragover") {
      setDragActive(true);
    } else if (e.type === "dragleave") {
      setDragActive(false);
    }
  };

  // Handle drop event
  const handleDrop = (e: React.DragEvent<HTMLDivElement>) => {
    e.preventDefault();
    e.stopPropagation();
    setDragActive(false);
    
    const droppedFiles = Array.from(e.dataTransfer.files).filter(
      file => file.type === "application/pdf"
    );
    
    if (droppedFiles.length > 0) {
      setSelectedFiles(droppedFiles);
    } else {
      onError("Only PDF files are accepted.");
    }
  };

  // Handle file input change
  const handleFileInputChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    if (e.target.files && e.target.files.length > 0) {
      const files = Array.from(e.target.files).filter(
        file => file.type === "application/pdf"
      );
      
      if (files.length > 0) {
        setSelectedFiles(files);
      } else {
        onError("Only PDF files are accepted.");
      }
    }
  };

  // Handle file upload and processing
  const handleProcessFiles = async () => {
    if (selectedFiles.length === 0) {
      onError("Please select at least one PDF file to upload.");
      return;
    }

    setUploading(true);
    onProcessingStart();
    setProgress(10); // Initial progress indicator
    
    try {
      const formData = new FormData();
      selectedFiles.forEach(file => {
        formData.append("files", file);
      });

      // Simulate progress
      const progressInterval = setInterval(() => {
        setProgress(prev => {
          const newProgress = prev + 5;
          return newProgress < 90 ? newProgress : prev;
        });
      }, 500);

      const response = await fetch(`/api/trips/${tripId}/process-pdfs`, {
        method: "POST",
        body: formData
      });

      clearInterval(progressInterval);
      setProgress(100);

      if (!response.ok) {
        const errorData = await response.json();
        throw new Error(errorData.message || "Failed to process PDF files.");
      }

      const data = await response.json();
      onProcessed(data.components);
    } catch (error) {
      console.error("Error processing PDFs:", error);
      onError(error instanceof Error ? error.message : "Failed to process PDF files.");
      setProgress(0);
      setUploading(false);
    }
  };

  // Handle button click to open file dialog
  const handleSelectFileClick = () => {
    fileInputRef.current?.click();
  };

  return (
    <div className="w-full">
      {/* Drag and drop area */}
      <div
        className={cn(
          "border-2 border-dashed rounded-lg p-8 text-center mb-4 cursor-pointer transition-colors",
          dragActive ? "border-primary bg-primary/5" : "border-gray-300 hover:border-gray-400"
        )}
        onDragEnter={handleDrag}
        onDragLeave={handleDrag}
        onDragOver={handleDrag}
        onDrop={handleDrop}
        onClick={handleSelectFileClick}
      >
        <FileUpIcon className="h-10 w-10 mx-auto mb-4 text-gray-400" />
        <p className="mb-2 text-sm text-gray-600">
          Drag and drop PDF files here, or click to select files
        </p>
        <p className="text-xs text-gray-500">
          Only PDF files are accepted
        </p>
        
        {/* Hidden file input */}
        <input
          ref={fileInputRef}
          type="file"
          accept=".pdf,application/pdf"
          multiple
          onChange={handleFileInputChange}
          className="hidden"
        />
      </div>

      {/* Selected files list */}
      {selectedFiles.length > 0 && (
        <div className="mb-4">
          <h3 className="text-sm font-medium mb-2">Selected Files ({selectedFiles.length})</h3>
          <ul className="space-y-1">
            {selectedFiles.map((file, index) => (
              <li key={index} className="text-sm bg-gray-50 p-2 rounded flex justify-between">
                <span className="truncate">{file.name}</span>
                <span className="text-gray-500">{(file.size / 1024).toFixed(1)} KB</span>
              </li>
            ))}
          </ul>
        </div>
      )}

      {/* Upload progress */}
      {uploading && (
        <div className="mb-4">
          <div className="flex justify-between mb-1">
            <span className="text-sm font-medium">Processing...</span>
            <span className="text-sm">{progress}%</span>
          </div>
          <Progress value={progress} className="h-2" />
          <p className="mt-2 text-sm text-gray-500">
            This may take a few moments as we extract and analyze the itinerary from your PDF.
          </p>
        </div>
      )}

      {/* Action buttons */}
      <div className="flex justify-end">
        <Button
          onClick={handleProcessFiles}
          disabled={selectedFiles.length === 0 || uploading}
          className="flex items-center gap-2"
        >
          {uploading ? (
            <>
              <Loader2 className="h-4 w-4 animate-spin" />
              Processing
            </>
          ) : (
            <>
              Process PDF{selectedFiles.length > 1 ? "s" : ""}
            </>
          )}
        </Button>
      </div>
    </div>
  );
}